/**
 * TAPD 提单模板切换时的 form 清理：必须 delete key，不能写 undefined。
 */

export const CAMEL_TO_SNAKE_CASE_FIELD_MAP = {
  severity: 'severity',
  reporter: 'reporter',
  versionReport: 'version_report',
  iterationId: 'iteration_id',
  releaseId: 'release_id',
  priority: 'priority',
  currentOwner: 'current_owner',
  module: 'module',
  baseline_find: 'baseline_find',
  os: 'os',
  frequency: 'frequency',
};

const SNAKE_TO_CAMEL = Object.fromEntries(
  Object.entries(CAMEL_TO_SNAKE_CASE_FIELD_MAP)
    .filter(([camel, snake]) => camel !== snake)
    .map(([camel, snake]) => [snake, camel]),
);

export const LEGACY_FEATURE_FORM_KEYS = [
  'severity', 'reporter', 'versionReport', 'iterationId', 'releaseId',
  'priority', 'currentOwner', 'module', 'baseline_find', 'os', 'frequency',
];

/** 标题/描述由 CrashSight 左侧表单或服务端生成，不当成模板列切来切去。 */
export const FEATURE_ANALYSIS_SKIP_TEMPLATE_FIELDS = {
  title: true,
  description: true,
  id: true,
};

/**
 * TAPD `get_default_bug_template` 经常不返回 reporter，但 POST /bugs 实际必填创建人。
 * 按模板渲染时补进列表最上面，并强制 required=1。
 */
export const TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER = 'reporter';

export function withAlwaysRequiredTemplateFields(templateFieldList, reporterDefaultValue) {
  const list = Array.isArray(templateFieldList) ? templateFieldList.filter((x) => x && x.field) : [];
  const existing = list.find((x) => x.field === TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER);
  const others = list.filter((x) => x.field !== TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER);
  const inheritedValue = existing && existing.value != null && existing.value !== ''
    ? existing.value
    : (reporterDefaultValue || '');
  return [
    {
      ...(existing || {}),
      field: TAPD_TEMPLATE_ALWAYS_REQUIRED_REPORTER,
      required: '1',
      sort: '-1',
      value: inheritedValue,
    },
    ...others,
  ];
}

/** TapdModal immer form 里必须在切模板时保留的非模板列。 */
export const TAPD_MODAL_RESERVED_FORM_KEYS = [
  'title',
  'description',
  'id',
  'isAlsoAddComment',
  'includeAttachments',
  'includeAiSolution',
  'attachmentFilenameList',
  'issueType',
  'jiraBugKey',
  'url',
  'JiraProject',
];

export function toSnakeField(fieldKey) {
  return CAMEL_TO_SNAKE_CASE_FIELD_MAP[fieldKey] || fieldKey;
}

export function aliasKeysOf(fieldKey) {
  const snake = toSnakeField(fieldKey);
  const camel = SNAKE_TO_CAMEL[snake];
  const keys = [fieldKey, snake];
  if (camel) {
    keys.push(camel);
  }
  return [...new Set(keys.filter(Boolean))];
}

export function readFormValue(formValue, fieldKey) {
  const src = formValue || {};
  for (const k of aliasKeysOf(fieldKey)) {
    if (src[k] !== undefined && src[k] !== null && src[k] !== '') {
      return src[k];
    }
  }
  return undefined;
}

/**
 * 只保留 nextVisibleKeys（及其 camel/snake 别名）。返回新对象，不改入参。
 */
export function omitKeysNotIn(formValue, nextVisibleKeys) {
  const keep = new Set();
  (nextVisibleKeys || []).forEach((k) => {
    aliasKeysOf(k).forEach((a) => keep.add(a));
  });
  const next = { ...(formValue || {}) };
  Object.keys(next).forEach((k) => {
    if (!keep.has(k)) {
      delete next[k];
    }
  });
  return next;
}

/** 每个可见字段只保留 preferred key（模板用 snake，历史 11 项用 camel），别名 delete。 */
export function collapseToPreferredKeys(formValue, preferredKeys) {
  const next = omitKeysNotIn(formValue, preferredKeys);
  (preferredKeys || []).forEach((k) => {
    const v = readFormValue(next, k);
    aliasKeysOf(k).forEach((a) => {
      if (a !== k) {
        delete next[a];
      }
    });
    if (v !== undefined) {
      next[k] = v;
    } else {
      delete next[k];
    }
  });
  return next;
}

/**
 * 按模板填充字段的默认值。
 *
 * 注意：**不要求字段必填**。TAPD 模板里大量字段是「非必填但有默认值」的（典型：处理人 current_owner，
 * 实测多数模板 required=0 但 value 有值），只填必填项会让这些默认值永远进不来。
 * 两条保留的约束：默认值为空不填；表单里已经有值不覆盖（不打断用户填过的内容）。
 * --story=1020421727138452632
 */
export function applyTemplateDefaults(formValue, fieldConfigs) {
  const next = { ...(formValue || {}) };
  (fieldConfigs || []).forEach(({ field, defaultValue }) => {
    if (defaultValue === undefined || defaultValue === null || defaultValue === '') {
      return;
    }
    // 「当前已有值」包含显式置空的 null（批量提单会把 version_report 显式置 null，
    // 表示各 issue 版本不同，此时不能被模板默认值统一覆盖）。
    if (hasAnyValue(next, field)) {
      return;
    }
    next[field] = defaultValue;
  });
  return next;
}

/** 字段（含 camel/snake 别名）在表单里是否已经有值。显式的 null 也算有值（显式置空）。 */
function hasAnyValue(formValue, fieldKey) {
  const src = formValue || {};
  return aliasKeysOf(fieldKey).some(k => src[k] !== undefined && src[k] !== '');
}

export function reconcileTemplateForm(prevValue, nextVisibleKeys, nextFieldConfigs) {
  return applyTemplateDefaults(collapseToPreferredKeys(prevValue, nextVisibleKeys), nextFieldConfigs);
}

/**
 * 只 delete 上一份模板里有、这一份没有的 key（及别名）。
 * TapdModal 的 immer form 还含 title/description/附件开关，不能用 omitKeysNotIn 整表裁。
 */
export function deleteRemovedTemplateKeys(formValue, goneKeys, reservedKeys) {
  const reserved = new Set(reservedKeys || TAPD_MODAL_RESERVED_FORM_KEYS);
  const next = { ...(formValue || {}) };
  (goneKeys || []).forEach((k) => {
    if (reserved.has(k) || FEATURE_ANALYSIS_SKIP_TEMPLATE_FIELDS[k]) {
      return;
    }
    aliasKeysOf(k).forEach((a) => {
      if (!reserved.has(a)) {
        delete next[a];
      }
    });
  });
  return next;
}

export function reconcileModalTemplateForm(formValue, goneKeys, nextFieldConfigs) {
  return applyTemplateDefaults(deleteRemovedTemplateKeys(formValue, goneKeys), nextFieldConfigs);
}

/** 给 antd setFieldsValue：被删的 key 设 null，才能清控件。 */
/** antd onValuesChange 里的 null/undefined 表示清控件，必须 delete，不能写回 form。 */
export function mergeFormChange(prevValue, changedValues) {
  const next = { ...(prevValue || {}) };
  Object.keys(changedValues || {}).forEach((k) => {
    const v = changedValues[k];
    if (v === null || v === undefined) {
      delete next[k];
    } else {
      next[k] = v;
    }
  });
  return next;
}

export function antdClearAndSet(removedKeys, nextValue) {
  const patch = {};
  (removedKeys || []).forEach((k) => {
    aliasKeysOf(k).forEach((a) => { patch[a] = null; });
  });
  Object.keys(nextValue || {}).forEach((k) => {
    if (nextValue[k] !== undefined) {
      patch[k] = nextValue[k];
    }
  });
  return patch;
}

/** 项目配置项「默认提单模板」的字面量：跟随TAPD项目默认模板（key不存在时同义） */
export const TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE = '__TAPD_DEFAULT__';
/** 项目配置项「默认提单模板」的字面量：不使用模板 */
export const TAPD_NO_TEMPLATE_VALUE = '__NONE__';

/** TAPD 模板列表里被标记为项目默认的那个模板（template_list 的 default 字段，Java 侧映射为 isDefault） */
export function findTapdDefaultTemplateId(templateList) {
  const hit = (templateList || []).find(x => x && x.isDefault === '1');
  return hit ? String(hit.id) : '';
}

/**
 * 根据项目配置的「默认提单模板」字面量 + 已拉取到的模板列表，算出表单打开时初始选中的模板。
 * @param {string|undefined} settingValue 配置项取值（undefined = 未配置）
 * @param {{id: string, isDefault: string}[]} templateList
 * @returns {{ templateId: string, invalidSetting: boolean }} templateId 为 '' 表示不使用模板；
 *          invalidSetting = 配了指定模板但列表里已经没有它（调用方应提示并回退到 TAPD 默认）
 */
export function resolveInitialTemplateId(settingValue, templateList) {
  const list = Array.isArray(templateList) ? templateList : [];
  const tapdDefaultId = findTapdDefaultTemplateId(list);
  if (settingValue === undefined || settingValue === null || settingValue === '') {
    return { templateId: tapdDefaultId, invalidSetting: false };
  }
  if (String(settingValue) === TAPD_NO_TEMPLATE_VALUE) {
    return { templateId: '', invalidSetting: false };
  }
  if (String(settingValue) === TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE) {
    return { templateId: tapdDefaultId, invalidSetting: false };
  }
  const configuredId = String(settingValue);
  const hit = list.find(x => x && String(x.id) === configuredId);
  if (hit) {
    return { templateId: configuredId, invalidSetting: false };
  }
  // 配置的模板在TAPD侧已经不存在（被删/换了项目）：回退到TAPD默认模板，并让调用方提示
  return { templateId: tapdDefaultId, invalidSetting: true };
}

export function removedKeys(prevVisibleKeys, nextVisibleKeys, reservedKeys) {
  const reserved = new Set(reservedKeys || []);
  const next = new Set();
  (nextVisibleKeys || []).forEach((k) => aliasKeysOf(k).forEach((a) => next.add(a)));
  return (prevVisibleKeys || []).filter((k) => (
    !next.has(k)
    && !next.has(toSnakeField(k))
    && !reserved.has(k)
    && !FEATURE_ANALYSIS_SKIP_TEMPLATE_FIELDS[k]
  ));
}
